import { defineStore } from 'pinia'

import type { IUser } from '~/entities/user/user/interfaces/user'

import type { MESSAGE_ID } from '~/features-shared/content/stores/contents'
import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'
import { logger } from '~/features-shared/libs/utils/logger'
import { getIdFromSubjectUri } from '~/features-shared/libs/utils/subject-uri-utils'
import * as RTConv from '~/features-shared/rtconv/api/rtinterface-senders'

import type { SwymResponse } from '~/interfaces/services-dto'

export const useCommentsStoreV2 = defineStore('comments-v2', () => {
  const endorsesMap: Map<MESSAGE_ID, string[]> = reactive(new Map())

  const currentUser_id = ref('')

  const getEndorseUri = (message_id: MESSAGE_ID, user_id: string) => `${message_id}-${user_id}`

  const getEndorses = (message_id: MESSAGE_ID) => endorsesMap.get(message_id)
  const setCurrentUser = (user_id: string) => {
    currentUser_id.value = user_id
  }
  const setEndorses = (message_id: MESSAGE_ID, usersWhoEndorsed: string[]) => {
    endorsesMap.set(message_id, usersWhoEndorsed)
  }
  const addEndorse = (message_id: MESSAGE_ID, user_id: string) => {
    const endorses = getEndorses(message_id)
    if (endorses) {
      if (!endorses?.includes(user_id))
        endorses.push(user_id)
    }
    else { setEndorses(message_id, [user_id]) }
  }
  const deleteEndorse = (message_id: MESSAGE_ID, user_id: string) => {
    const endorses = getEndorses(message_id)
    if (endorses) {
      const index = endorses.findIndex(id => id === user_id)
      if (index !== -1)
        endorses?.splice(index, 1)

      if (endorses.length === 0)
        endorsesMap.delete(message_id)
    }
  }
  const deleteAllEndorses = (message_id: MESSAGE_ID) => {
    endorsesMap.delete(message_id)
  }

  /**
   * Get comments stats (nbEndorses, current user)
   * @param subject_uri - Subject uri of the endorsed message.
   * @returns Comments stats.
   */
  const getComments = async (subject_uri: SUBJECT_6W_URI) => {
    const message_id = getIdFromSubjectUri(subject_uri)

    const endorses = getEndorses(message_id)
    const endorseUri = endorses?.includes(currentUser_id.value)
      ? getEndorseUri(message_id, currentUser_id.value)
      : ''
    return {
      nbComments: 0, // not implemented
      nbEndorses: endorses?.length ?? 0,
      currentUserEndorse: { endorseUri },
    }
  }

  /**
   * Add endorse of current user.
   * @param subject_uri - Subject uri of the endorsed message.
   * @param thread_id - Subject uri or id of the conversation.
   * @returns Endorse uri.
   */
  const addMyEndorse = async (subject_uri: SUBJECT_6W_URI, thread_id: string) => {
    const message_id = getIdFromSubjectUri(subject_uri)

    try {
      const user_id = await RTConv.likeMessage({
        convId: Number(getIdFromSubjectUri(thread_id)),
        msgId: Number(message_id),
        liked: true,
        waitForResponse: true,
      })
      return { endorseUri: getEndorseUri(message_id, user_id) }
    }
    catch (error) {
      logger.error(error)
    }
  }

  /**
   * Remove endorse of current user.
   * @param options - Options.
   * @param options.subject_uri - Subject uri of the endorsed message.
   * @param options.endorse_uri - Endorse uri. Unused because message is removed
   * @param options.thread_id - Subject uri or id of the conversation.
   * @returns Promise fulfilling on RTC response.
   */
  const removeMyEndorse = async (options: {
    subject_uri: SUBJECT_6W_URI
    endorse_uri: string
    thread_id: string
  }) => {
    const { subject_uri, thread_id } = options
    const message_id = getIdFromSubjectUri(subject_uri)

    try {
      return RTConv.likeMessage({
        convId: Number(getIdFromSubjectUri(thread_id)),
        msgId: Number(message_id),
        liked: false,
        waitForResponse: true,
      })
    }
    catch (error) {
      logger.error(error)
    }
  }

  /**
   * Get users who endorsed a message in a conversation.
   * @param options - Options.
   * @param options.subject_uri - Subject uri of the message.
   * @param options.thread_id - Subject uri or id of the conversation.
   * @param options.members - Members of conversation. Will be fetched from RTC if not specified.
   * @returns List of users who endorse.
   */
  const getUsersWhoEndorse = async ({ subject_uri, thread_id, members }:
  { subject_uri: SUBJECT_6W_URI, thread_id: SUBJECT_6W_URI | string, members?: IUser[] }):
  Promise<SwymResponse<IUser[]>> => {
    const message_id = getIdFromSubjectUri(subject_uri)
    const endorses = getEndorses(message_id)
    //wrap as Swym response so nb_result can be used to know if pagination is needed
    const wrapAsSwymResult = (usersArray: IUser[]) => ({ nb_result: `${usersArray.length}`, result: usersArray })

    if (!endorses || endorses?.length === 0)
      return wrapAsSwymResult([])

    try {
      if (!members) {
        members = await RTConv.getLikes({
          convId: Number(getIdFromSubjectUri(thread_id)),
          msgId: Number(message_id),
          waitForResponse: true,
        })
      }
    }
    catch (error) {
      logger.error(error)
    }

    if (!members)
      return wrapAsSwymResult([])
    const usersWhoEndorse = members.filter(u => u.id && endorses.includes(u.id))
    return wrapAsSwymResult(usersWhoEndorse)
  }

  return {
    setCurrentUser,
    setEndorses,
    addEndorse,
    deleteEndorse,
    deleteAllEndorses,

    getComments,
    addMyEndorse,
    removeMyEndorse,
    getUsersWhoEndorse,
  }
})
